import { useCallback } from 'react';

export type TreeNavigationOptions = {
  containerRef: React.RefObject<HTMLElement>;
  headerSelector: string;
  itemSelector: string;
  onHeaderExpand?: (header: HTMLElement) => void;
  onHeaderCollapse?: (header: HTMLElement) => void;
  onItemSelect?: (item: HTMLElement) => void;
};

export function useTreeNavigation({
  containerRef,
  headerSelector,
  itemSelector,
  onHeaderExpand,
  onHeaderCollapse,
  onItemSelect,
}: TreeNavigationOptions) {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!containerRef.current || !document.activeElement) {
        return;
      }

      const isHeader = document.activeElement.matches(headerSelector);

      const currentRegion = isHeader
        ? document.activeElement.getAttribute('aria-controls')
        : document.activeElement.closest('[role="region"]')?.id;

      if (!currentRegion) {
        return;
      }

      const focusAndSelect = (element: HTMLElement) => {
        element.focus();

        const isHeader = element.matches(headerSelector);

        if (!isHeader && onItemSelect) {
          onItemSelect(element);
        }
      };

      switch (event.key) {
        case 'ArrowLeft': {
          event.preventDefault();

          if (isHeader) {
            // collapse current group
            const header = document.activeElement as HTMLElement;

            if (header.getAttribute('aria-expanded') === 'true') {
              onHeaderCollapse?.(header);
            }
          } else {
            // move focus to parent header and collapse
            const header = document.activeElement
              .closest('[role="listitem"]')
              ?.querySelector<HTMLElement>(headerSelector);

            if (header) {
              header.focus();

              if (header.getAttribute('aria-expanded') === 'true') {
                onHeaderCollapse?.(header);
              }
            }
          }

          break;
        }

        case 'ArrowRight': {
          event.preventDefault();

          if (isHeader) {
            // expand current group or move to first item
            const header = document.activeElement as HTMLElement;

            if (header.getAttribute('aria-expanded') === 'false') {
              onHeaderExpand?.(header);
            } else {
              // if already expanded, move to first item
              const firstItem = document.getElementById(currentRegion)?.querySelector<HTMLElement>(itemSelector);

              if (firstItem) {
                focusAndSelect(firstItem);
              }
            }
          }

          break;
        }

        case 'ArrowDown': {
          event.preventDefault();

          if (isHeader) {
            // move to first item in region or next header
            const firstItem = document.getElementById(currentRegion)?.querySelector<HTMLElement>(itemSelector);

            if (firstItem) {
              focusAndSelect(firstItem);
            } else {
              const nextHeader =
                document.activeElement.parentElement?.nextElementSibling?.querySelector<HTMLElement>(headerSelector);

              nextHeader?.focus();
            }
          } else {
            // move to next item in region or next header
            const nextItem = document.activeElement.nextElementSibling as HTMLElement;

            if (nextItem?.matches(itemSelector)) {
              focusAndSelect(nextItem);
            } else {
              const nextHeader = document.activeElement
                .closest('[role="listitem"]')
                ?.nextElementSibling?.querySelector<HTMLElement>(headerSelector);

              nextHeader?.focus();
            }
          }

          break;
        }

        case 'ArrowUp': {
          event.preventDefault();

          if (isHeader) {
            // move to last item of previous region or previous header
            const prevRegion =
              document.activeElement.parentElement?.previousElementSibling?.querySelector<HTMLElement>(
                '[role="region"]',
              );

            const lastItem = prevRegion?.querySelector<HTMLElement>(`${itemSelector}:last-child`);

            if (lastItem) {
              focusAndSelect(lastItem);
            } else {
              const prevHeader =
                document.activeElement.parentElement?.previousElementSibling?.querySelector<HTMLElement>(
                  headerSelector,
                );

              prevHeader?.focus();
            }
          } else {
            // move to previous item or region header
            const prevItem = document.activeElement.previousElementSibling as HTMLElement;

            if (prevItem?.matches(itemSelector)) {
              focusAndSelect(prevItem);
            } else {
              const header = document.activeElement
                .closest('[role="listitem"]')
                ?.querySelector<HTMLElement>(headerSelector);

              header?.focus();
            }
          }

          break;
        }
      }
    },
    [containerRef, headerSelector, itemSelector, onHeaderCollapse, onHeaderExpand, onItemSelect],
  );

  return { handleKeyDown };
}
